import type { SceneId, SongSection, TransitionType, VisualEvent, VisualStyle } from '../types';
import { SCENE_IDS } from '../types';
import { hashString, pickFrom, seededRandom } from './hash';
import { sectionAt, sectionIntensity, type SectionSpan } from './sections';

const TARGET_SCENE_MS = 60_000;
const MIN_SCENE_MS = 42_000;
const MAX_SCENE_MS = 74_000;

const SECTION_SCENES: Record<SongSection, SceneId[]> = {
  intro: ['cathedral-of-light', 'starfield-warp', 'particle-galaxy', 'album-art-explosion', 'bird-flight'],
  verse: ['cyberpunk-city', 'waveform-terrain', 'laser-grid', 'cathedral-of-light'],
  build: ['cathedral-of-light', 'neon-tunnel', 'starfield-warp', 'laser-grid', 'bird-flight'],
  chorus: ['cathedral-of-light', 'festival-crowd', 'kinetic-typography', 'lyric-constellation', 'album-art-explosion', 'bird-flight'],
  drop: ['cathedral-of-light', 'neon-tunnel', 'bass-crystal', 'particle-galaxy', 'album-art-explosion'],
  bridge: ['cathedral-of-light', 'sacred-geometry', 'bass-crystal', 'particle-galaxy', 'bird-flight'],
  outro: ['cathedral-of-light', 'album-art-explosion', 'starfield-warp', 'sacred-geometry', 'bird-flight'],
};

const SECTION_TRANSITIONS: Record<SongSection, TransitionType> = {
  intro: 'fade',
  verse: 'fade',
  build: 'zoom',
  chorus: 'flash',
  drop: 'cut',
  bridge: 'fade',
  outro: 'fade',
};

const STYLE_SCENE_BIAS: Partial<Record<VisualStyle, SceneId[]>> = {
  festival: ['cathedral-of-light', 'festival-crowd', 'neon-tunnel', 'particle-galaxy'],
  psychedelic: ['sacred-geometry', 'particle-galaxy', 'kinetic-typography'],
  plasma: ['plasma-visualizer-2d', 'particle-galaxy', 'waveform-terrain', 'sacred-geometry', 'album-art-explosion'],
  vortex: ['vortex-visualizer-2d', 'neon-tunnel', 'starfield-warp', 'particle-galaxy', 'sacred-geometry'],
  'strobe-burst': ['strobe-visualizer-2d', 'festival-crowd', 'neon-tunnel', 'bass-crystal', 'album-art-explosion'],
  flash: ['flash-visualizer-2d', 'kinetic-typography', 'festival-crowd', 'laser-grid', 'bass-crystal'],
  'neon-journey': ['journey-2-visualizer-2d', 'journey-visualizer-2d', 'cyberpunk-city', 'neon-tunnel', 'bird-flight', 'laser-grid'],
  cinematic: ['cathedral-of-light', 'bird-flight', 'starfield-warp', 'cyberpunk-city', 'waveform-terrain'],
  minimal: ['laser-grid', 'waveform-terrain', 'bass-crystal'],
  emotional: ['cathedral-of-light', 'bird-flight', 'lyric-constellation', 'kinetic-typography', 'album-art-explosion'],
  'horizon-journey': ['journey-visualizer-2d', 'journey-2-visualizer-2d', 'bird-flight', 'waveform-terrain', 'cathedral-of-light', 'starfield-warp'],
  aggressive: ['cathedral-of-light', 'neon-tunnel', 'bass-crystal', 'festival-crowd'],
  cyberpunk: ['cyberpunk-city', 'laser-grid', 'neon-tunnel', 'cathedral-of-light'],
  space: ['cathedral-of-light', 'bird-flight', 'starfield-warp', 'particle-galaxy', 'lyric-constellation'],
};

function chooseScene(
  section: SongSection,
  rand: () => number,
  style: VisualStyle,
  used: Set<SceneId>
): SceneId {
  const pool = [...SECTION_SCENES[section]];
  const bias = STYLE_SCENE_BIAS[style] ?? [];
  for (const scene of bias) {
    if (SCENE_IDS.includes(scene)) pool.unshift(scene);
  }
  const fresh = pool.filter((s) => !used.has(s));
  const pick = fresh.length > 0 ? fresh : pool;
  return pickFrom(pick, rand);
}

export function buildVisualTimeline(
  trackId: string,
  durationMs: number,
  sections: SectionSpan[],
  style: VisualStyle = 'festival',
  audioIntensity = 0.75
): VisualEvent[] {
  const seed = hashString(`${trackId}:${style}`);
  const rand = seededRandom(seed);
  const events: VisualEvent[] = [];
  const used = new Set<SceneId>();

  let cursor = 0;
  while (cursor < durationMs) {
    const remainingMs = durationMs - cursor;
    const section = sectionAt(sections, cursor + Math.min(remainingMs, TARGET_SCENE_MS * 0.45));
    const sceneId = chooseScene(section, rand, style, used);
    used.add(sceneId);
    if (used.size > 7) used.delete(pickFrom([...used], rand));

    const driftMs = (rand() - 0.5) * 12_000;
    let sceneMs = Math.max(MIN_SCENE_MS, Math.min(MAX_SCENE_MS, TARGET_SCENE_MS + driftMs));
    if (remainingMs < MIN_SCENE_MS && events.length > 0) {
      events[events.length - 1].endMs = durationMs;
      break;
    }
    if (remainingMs < TARGET_SCENE_MS * 1.35) sceneMs = remainingMs;

    const endMs = Math.min(durationMs, cursor + sceneMs);
    const intensity = Math.max(
      0.18,
      Math.min(1.25, sectionIntensity(section) * (0.72 + audioIntensity * 0.48) * (0.9 + rand() * 0.2))
    );

    events.push({
      startMs: cursor,
      endMs,
      sceneId,
      section,
      transition: SECTION_TRANSITIONS[section],
      intensity,
      params: {
        pulseRate: section === 'drop' ? 2 : 1,
        lyricMode: section === 'chorus' || section === 'drop',
        strobe: section === 'drop' ? 1 : 0,
        tunnelSpeed: section === 'build' ? 1.4 : 1,
        journey: true,
        seed: Math.floor(rand() * 10000),
      },
    });

    cursor = endMs;
  }

  if (events.length === 0) {
    events.push({
      startMs: 0,
      endMs: durationMs,
      sceneId: SCENE_IDS[seed % SCENE_IDS.length],
      section: 'verse',
      transition: 'fade',
      intensity: 0.6,
      params: { seed },
    });
  }

  return events;
}

export function activeVisualEvent(
  timeline: VisualEvent[],
  progressMs: number
): VisualEvent | null {
  return timeline.find((e) => progressMs >= e.startMs && progressMs < e.endMs)
    ?? timeline[timeline.length - 1]
    ?? null;
}

export function nextVisualEvent(
  timeline: VisualEvent[],
  progressMs: number
): VisualEvent | null {
  return timeline.find((e) => e.startMs > progressMs) ?? null;
}
